<template>
  <div class="input-editor-container" :class="alignContent">
    <EditorContent
      :class="['prosemirror-wrapper', { 'hide-placeholder': hidePlaceholder }]"
      :editor="editor"
      spellcheck="true"
    />
  </div>
</template>
<script lang="ts">
import { useDebounceFn } from '@vueuse/core';
import { Editor as TipTapEditor, EditorContent } from '@tiptap/vue-3';
import Placeholder from '@tiptap/extension-placeholder';
import Text from '@tiptap/extension-text';
import {
  HeadingDocNode,
  HeadingLevel,
  HeadingNode,
  InputDocNode,
  ParagraphNode,
  ValueAlign,
} from '@getaccept/editor-lib';
import { useI18n } from 'vue-i18n';
import type { PropType, Ref } from 'vue';
import { watch, nextTick, ref, onBeforeUnmount, onMounted, defineComponent, computed } from 'vue';

export default defineComponent({
  components: {
    EditorContent,
  },
  props: {
    content: { type: String, default: '' },
    selected: { type: Boolean },
    inputState: { type: Object as PropType<{ focus: boolean }>, default: () => ({ focus: false }) },
    alignContent: { type: String as PropType<ValueAlign>, default: ValueAlign.Right },
    placeholder: { type: String, default: '' },
    hidePlaceholder: { type: Boolean, default: true },
    heading: { type: Number as PropType<HeadingLevel>, default: () => null },
  },
  emits: ['update', 'focus'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const editor: Ref<TipTapEditor | null> = ref(null);
    const active = ref(false);

    const update = () => {
      if (!editor.value.view.hasFocus()) {
        active.value = false;
      }
      const content = editor.value.getText();

      if (props.content === content) {
        return;
      }
      emit('update', content);
    };

    const debounceUpdate = useDebounceFn(update, 500);

    const handleFocus = () => {
      emit('focus');
      active.value = true;
    };

    const setFocus = () => {
      if (!editor.value.view.hasFocus()) {
        active.value = true;
        editor.value.view.focus();
      }
    };

    const handleInit = () => {
      if (!props.selected) {
        return;
      }
      nextTick(() => {
        setFocus();
      });
    };

    const inputEditor = computed(() => ({
      content: props.content,
      enableInputRules: false,
      enablePasteRules: false,
      extensions: [
        props.heading ? HeadingDocNode : InputDocNode,
        ParagraphNode,
        Text,
        Placeholder.configure({
          placeholder: t(props.placeholder),
        }),
        ...(props.heading
          ? [
              HeadingNode.configure({
                defaultLevel: HeadingLevel.Heading2,
              }),
            ]
          : []),
      ],
      onCreate: handleInit,
      onBlur: debounceUpdate,
      onUpdate: debounceUpdate,
      onFocus: handleFocus,
      parseOptions: {
        preserveWhitespace: true,
      },
    }));

    onMounted(() => {
      editor.value = new TipTapEditor(inputEditor.value);
    });

    onBeforeUnmount(() => {
      editor.value.destroy();
    });

    watch(
      () => props.content,
      content => {
        if (active.value || content === editor.value.getText()) {
          return;
        }
        editor.value.commands.setContent(content);
      }
    );

    watch(
      () => props.inputState,
      inputState => {
        if (inputState.focus) {
          setFocus();
        }
      }
    );

    return {
      editor,
      handleFocus,
      setFocus,
      handleInit,
      inputEditor,

      update,
    };
  },
});
</script>
<style lang="scss" scoped>
/* stylelint-disable selector-class-pattern */
.input-editor-container {
  width: 100%;
  padding-right: var(--spacing-25);

  :deep(.ProseMirror) {
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
    outline: 0;
    word-break: break-word;
    border-radius: var(--radius-medium);
    padding: 0 var(--spacing-25) 0;

    .is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      color: var(--gray-60);
      pointer-events: none;
      height: 0;
    }

    .is-editor-empty:first-child {
      br {
        display: none;
      }
    }

    p::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      border-radius: var(--radius-medium);
    }

    &:hover {
      background: var(--gray-96);
    }

    &:focus {
      background: initial;
    }

    &.ProseMirror-focused {
      p::after {
        border: 1px solid var(--gray-96);
      }
    }
  }

  &.left {
    justify-content: flex-start;

    :deep(.ProseMirror) {
      justify-content: flex-start;
    }
  }

  .hide-placeholder {
    :deep(.ProseMirror) {
      &.ProseMirror-focused {
        .is-editor-empty:first-child::before {
          content: ' ';
        }
      }
    }
  }
}
</style>
